<template>
  <div class="lzd-editor-wrapper" :class="{ 'is-wrapper-focus': isFocused }">
    <div
      v-if="!readOnly && type === 'richtext'"
      class="formula-floating-btn"
      @mousedown.prevent
    >
      <el-button
        circle
        size="small"
        class="math-btn"
        @click="showMathDialog = true"
      >
        <span class="math-icon">∑</span>
      </el-button>
    </div>

    <div ref="editorRef" class="lzd-wang-editor"></div>

    <MathLiveDialog v-model="showMathDialog" @insert="handleMathInsert" />
  </div>
</template>

<script lang="ts" setup>
  import {
    watch,
    onMounted,
    onBeforeUnmount,
    onActivated,
    onDeactivated,
    ref,
    toRaw
  } from 'vue';
  import {
    customPasteOnlyText,
    defaultEditorConfig,
    renderEditorNext,
    type LzdDescendant,
    type LzdEditorConfig,
    type LzdEditorElement
  } from '@/utils/lzd-editor';
  import type { IDomEditor } from '@wangeditor/editor';
  import { createEditor, createToolbar } from '@wangeditor/editor';

  // --- 公式相关引入 ---
  import { Boot } from '@wangeditor/editor';
  import formulaModule from '@wangeditor/plugin-formula';
  import 'katex/dist/katex.min.css';
  import katex from 'katex';

  import MathLiveDialog from '@/components/lzd/math-live-dialog/index.vue';

  import { debounce, JSONUtil } from '@/utils/lzd-util';
  import { isEqual } from 'lodash-es';
  import { useFormItem } from 'element-plus';

  // 注册公式插件
  try {
    Boot.registerModule(formulaModule);
  } catch (e) {
    console.log('Formula module already registered');
  }

  type ValueType = LzdDescendant[] | string | number;

  /** 属性 */
  const props = withDefaults(
    defineProps<{
      /** v-model */
      modelValue?: ValueType;
      /** 编辑器名称 */
      name?: string;
      /** 编号 */
      index?: number;
      /** 编辑器类型 */
      type?: 'input' | 'textarea' | 'number' | 'richtext';
      /** 值类型 */
      format?: 'json' | 'text' | 'html';
      /** 最小值 */
      min?: number;
      /** 最大值 */
      max?: number;
      /** 最多字符数 */
      maxLength?: number;
      /** 只读 */
      readOnly?: boolean;
      /** 隐藏边框阴影 */
      hideBoxShadow?: boolean;
      /** 隐藏边框 */
      hideBorder?: boolean;
      /** 最小高度 */
      minHeight?: string | number;
      /** 垂直居中 */
      verticalCenter?: boolean;
      /** 水平居中 */
      horizontalCenter?: boolean;
      /** 占位提示符 */
      placeholder?: string;
      /** 选中显示的菜单 */
      hoverbarKeys?: any;
      /** 其他配置 */
      config?: LzdEditorConfig;
      /** 点击父级获取焦点 */
      focusByParent?: boolean;
      /** 回调函数 */
      created?: (editor: IDomEditor, name?: string) => void;
      change?: (editor: IDomEditor, name?: string) => void;
      focus?: (editor: IDomEditor, name?: string) => void;
      blur?: (editor: IDomEditor, name?: string) => void;
      /** 是否显示工具栏 */
      showToolbar?: boolean;
      /** 排除工具栏哪些菜单 */
      excludeKeys?: string[];
    }>(),
    {
      index: 0,
      readOnly: false,
      minHeight: '150px',
      verticalCenter: false,
      horizontalCenter: false,
      placeholder: '',
      type: 'textarea',
      format: 'text',
      focusByParent: true,
      hideBoxShadow: true,
      hideBorder: true,
      hoverbarKeys: {
        image: {
          menuKeys: ['deleteImage']
        },
        text: {
          menuKeys: ['bold', 'sub', 'sup', 'clearStyle']
        }
      },
      showToolbar: false
    }
  );

  /** 事件 */
  const emit = defineEmits<{
    (e: 'update:modelValue', value: ValueType): void;
    (e: 'created', value?: IDomEditor, name?: string): void;
    (e: 'change', value?: IDomEditor, name?: string): void;
    (e: 'focus', value?: IDomEditor, name?: string): void;
    (e: 'blur', value?: IDomEditor, name?: string): void;
  }>();

  const toolbarRef = ref<HTMLDivElement>();
  const editorRef = ref<LzdEditorElement>();
  const { formItem } = useFormItem();

  /** 编辑器字符数 */
  const editorChars = defineModel<number>('chars');

  const showMathDialog = ref(false);
  const editorInstanceRef = ref<IDomEditor | null>(null);
  // 控制聚焦状态
  const isFocused = ref(false);

  const modelValue = toRaw(props.modelValue);
  const modelValueIsArr = Array.isArray(modelValue);
  let currContent: ValueType =
    modelValue ?? (props.format !== 'json' || !modelValueIsArr ? '' : []);

  const updateContentDebounce = debounce();
  const updateModelValue = (value: ValueType, fn?: () => void) => {
    if (
      !modelValueIsArr ||
      props.format !== 'json' ||
      typeof props.modelValue === 'string' ||
      typeof props.modelValue === 'number'
    ) {
      emit('update:modelValue', value);
    } else {
      emit('update:modelValue', value as LzdDescendant[]);
    }
    fn && fn();
  };

  // 处理 MathLive 插入回调
  const handleMathInsert = (latex: string) => {
    const editor = editorInstanceRef.value;
    if (!editor) return;
    editor.restoreSelection();
    editor.insertNode({
      type: 'formula',
      value: latex,
      children: [{ text: '' }]
    } as any);
    editor.focus();
  };

  /** 渲染编辑器 */
  let isChange = false;
  const render = () => {
    const el = editorRef.value!;
    el.classList.remove('is-focus');
    el.lzdEditorUpdate = el.lzdEditorUpdate ?? debounce();
    el.lzdEditorUpdate(async () => {
      // 等待依次渲染
      await renderEditorNext((currId) => {
        el.lzdEditorInstance?.destroy();
        el.innerHTML = '';
        el.style.visibility = 'hidden';

        const config = { ...defaultEditorConfig(), ...props.config };
        config.type = props.type ?? config.type;
        config.format = props.format ?? config.format;
        config.readOnly = props.readOnly ?? config.readOnly;
        config.placeholder = props.placeholder ?? config.placeholder;

        config.hoverbarKeys = {
          ...(props.hoverbarKeys ?? config.hoverbarKeys ?? {}),
          formula: {
            menuKeys: ['editFormula']
          }
        };

        if (config.readOnly) {
          config.placeholder = '';
        }
        const onCreated = props.created ?? config.onCreated;
        const onChange = props.change ?? config.onChange;
        const onFocus = props.focus ?? config.onFocus;
        const onBlur = props.blur ?? config.onBlur;
        el.style.minHeight = (props.minHeight ??
          config.minHeight ??
          '300px') as string;
        el.classList.add('lzd-wang-editor');
        props.verticalCenter && el.classList.add('vertical-center');
        props.horizontalCenter && el.classList.add('horizontal-center');
        props.hideBoxShadow && el.classList.add('hide-box-shadow');
        props.hideBorder && el.classList.add('hide-border');
        if (config.readOnly) {
          el.classList.add('readOnly');
        } else {
          el.classList.remove('readOnly');
        }

        const editorType = config.type ?? 'richtext';

        // 不是富文本的处理
        if ('richtext' !== editorType) {
          config.hoverbarKeys = {
            text: { menuKeys: [] }
          };
          config.customPaste = (editor: IDomEditor, e: ClipboardEvent) => {
            return customPasteOnlyText(editorType, editor, e);
          };
        }

        config.onCreated = (editor: IDomEditor) => {
          editorInstanceRef.value = editor;

          countValidCharsStrict(editor);
          el.id = `lzd-editor-${currId}`;
          if (config.readOnly) {
            const html = editor.getHtml();
            editor.destroy();
            el.innerHTML = html
              .replace(/<script>/gi, '&lt;script&gt;')
              .replace(/<script/gi, '&lt;script')
              .replace(/<\/script>/gi, '&lt;/script&gt;');

            // 渲染 LaTeX 公式
            const formulaNodes = el.querySelectorAll(
              '[data-w-e-type="formula"]'
            );
            formulaNodes.forEach((node) => {
              const latex = node.getAttribute('data-value');
              if (latex) {
                node.innerHTML = renderLatexHTML(latex);
              }
            });

            el.style.visibility = '';
            return;
          }
          el.classList.add('is-editor');
          el.style.visibility = '';
          el.focus = () => editor.focus();
          emit('created', editor, props.name);
          if (onCreated) {
            onCreated(editor, props.name);
          }
          if (props.focusByParent) {
            editorRef.value?.parentElement?.addEventListener('click', () => {
              editor.focus();
            });
          }
        };

        config.onChange = (editor: IDomEditor) => {
          countValidCharsStrict(editor);
          let _fn = () => {
            isChange = true;
            emit('change', editor, props.name);
            if (onChange) {
              onChange(editor, props.name);
            }
            if (formItem) {
              formItem.validate('change');
            }
          };

          let currValue;
          if (props.format === 'json' && modelValueIsArr) {
            currValue = editor.children as LzdDescendant[];
          } else {
            currValue =
              props.format === 'json'
                ? JSONUtil.toStr(editor.children)
                : props.format === 'html'
                  ? editor.getHtml()
                  : editor.getText();
          }

          if (isEqual(currContent, currValue)) {
            return;
          }
          currContent = currValue;
          updateContentDebounce(() => updateModelValue(currContent, _fn), 350);
        };

        config.onFocus = (editor: IDomEditor) => {
          isFocused.value = true;
          !props.hideBoxShadow && el.classList.add('is-focus');
          emit('focus', editor, props.name);
          if (onFocus) {
            onFocus(editor, props.name);
          }
        };

        config.onBlur = (editor: IDomEditor) => {
          isFocused.value = false;
          el.classList.remove('is-focus');
          let _fn = () => {
            emit('blur', editor, props.name);
            if (onBlur) {
              onBlur(editor, props.name);
            }
            isChange = false;
            if (formItem) {
              formItem.validate('blur');
            }
          };

          let currValue;
          if (props.format === 'json' && modelValueIsArr) {
            currValue = editor.children as LzdDescendant[];
          } else {
            currValue =
              props.format === 'json'
                ? JSONUtil.toStr(editor.children)
                : props.format === 'html'
                  ? editor.getHtml()
                  : editor.getText();
          }

          if (isChange && isEqual(currContent, currValue)) {
            _fn();
            return;
          }
          currContent = currValue;
          updateContentDebounce(() => updateModelValue(currContent, _fn), 350);
        };

        if (editorType === 'number') {
          el.classList.add('number');
          const inputEl = document.createElement('input');
          inputEl.type = 'number';
          inputEl.className = 'lzd-wang-editor-number';
          inputEl.step = '1';
          if (typeof currContent !== 'undefined')
            inputEl.value = String(currContent);
          if (typeof props.min !== 'undefined') inputEl.min = String(props.min);
          if (typeof props.max !== 'undefined') inputEl.max = String(props.max);
          if (typeof config.placeholder !== 'undefined')
            inputEl.placeholder = config.placeholder;

          const editorCallback: any = {
            id: 'lzdEditId-' + currId,
            getText: () => inputEl.value,
            getHtml: () => inputEl.value,
            destroy: () => inputEl.remove(),
            children: [
              { type: 'paragraph', children: [{ text: inputEl.value }] }
            ]
          };
          inputEl.oninput = () => {
            config.onChange!(editorCallback);
          };
          el.appendChild(inputEl);
          config.onCreated!(editorCallback);
          el.lzdEditorInstance = editorCallback;
          el.focus = () => inputEl.focus();
          return;
        }

        const commonToolbarConfig = {
          excludeKeys: props.excludeKeys || [],
          insertKeys: {
            index: 0,
            keys: ['insertFormula']
          }
        };

        if (config.format === 'json') {
          let content;
          const editorContentRenderData: LzdDescendant[] = [];
          if (
            !/^[\{\[]/.test(currContent as string) ||
            !/[\}\]]$/.test(currContent as string)
          ) {
            const currContentStr = String(currContent ?? '');
            currContentStr.split(/\r?\n/).forEach((txt) =>
              editorContentRenderData.push({
                type: 'paragraph',
                children: [{ text: txt ?? '' }]
              })
            );
            content = editorContentRenderData;
          } else {
            content = modelValueIsArr
              ? currContent
              : JSONUtil.parse(currContent as string);
          }

          const editor = createEditor({
            selector: el,
            html: '<p><br></p>',
            content: content as any,
            config: config,
            mode: 'default'
          });

          if (props.showToolbar) {
            const xtoolbarEl = document.createElement('div');
            el.parentNode?.insertBefore(xtoolbarEl, el);
            toolbarRef.value = xtoolbarEl;
            createToolbar({
              editor,
              selector: toolbarRef.value!,
              config: commonToolbarConfig,
              mode: 'default'
            });
          }
          el.lzdEditorInstance = editor;
        } else if (config.format === 'html') {
          const editor = createEditor({
            selector: el,
            html: (currContent as string) ?? '<p><br></p>',
            config: config,
            mode: 'default'
          });

          if (props.showToolbar) {
            const xtoolbarEl = document.createElement('div');
            el.parentNode?.insertBefore(xtoolbarEl, el);
            toolbarRef.value = xtoolbarEl;
            createToolbar({
              editor,
              selector: toolbarRef.value!,
              config: commonToolbarConfig,
              mode: 'default'
            });
          }
          el.lzdEditorInstance = editor;
        } else {
          const editorContentRenderData: LzdDescendant[] = [];
          const currContentStr = String(currContent ?? '');
          if (config.type !== 'textarea') {
            editorContentRenderData.push({
              type: 'paragraph',
              children: [{ text: currContentStr }]
            });
          } else {
            currContentStr.split(/\r?\n/).forEach((txt) =>
              editorContentRenderData.push({
                type: 'paragraph',
                children: [{ text: txt ?? '' }]
              })
            );
          }

          let editor: any = null;
          try {
            editor = createEditor({
              selector: el,
              html: '<p><br></p>',
              config: config,
              content: editorContentRenderData,
              mode: 'default'
            });
          } catch (err) {
            console.error(err, el);
            return;
          }

          if (props.showToolbar) {
            const xtoolbarEl = document.createElement('div');
            el.parentNode?.insertBefore(xtoolbarEl, el);
            toolbarRef.value = xtoolbarEl;
            createToolbar({
              editor,
              selector: toolbarRef.value!,
              config: commonToolbarConfig,
              mode: 'default'
            });
          }
          el.lzdEditorInstance = editor;
        }

        if ('input' === editorType) {
          el.onkeydown = (e) => {
            if ('Enter' === e.code) {
              e.preventDefault();
              return false;
            }
          };
        }
      });
    }, 0);
  };

  /** 销毁编辑器 */
  const destory = () => {
    if (editorRef.value) {
      editorRef.value.lzdEditorInstance?.destroy();
      editorRef.value.lzdEditorInstance = null;
    }
  };

  /**
   * 渲染 LaTeX 为 HTML
   */
  const renderLatexHTML = (latex: string) => {
    try {
      return katex.renderToString(latex, {
        throwOnError: false,
        displayMode: false
      });
    } catch (err) {
      return latex;
    }
  };

  // 字符统计
  const countValidCharsStrict = (editor: IDomEditor) => {
    const editable = editorRef.value?.querySelector(
      '[contenteditable="true"]'
    ) as HTMLElement;

    if (props.type === 'number') {
      if (editable) editable.style.paddingBottom = '0px';
      return;
    }

    const text = editor.getText();
    const count = text
      .replace(/[\p{P}\p{S}]/gu, (char) => (char === '/' ? char : ''))
      .replace(/\s/g, '').length;
    editorChars.value = count;

    const w_e_max_length_info = editorRef.value?.querySelector(
      '.w-e-max-length-info'
    ) as HTMLElement;

    if (!w_e_max_length_info) {
      if (editable) editable.style.paddingBottom = '0px';
      return;
    }

    const showLimit = !!(props.max || props.min);
    w_e_max_length_info.style.display = showLimit ? '' : 'none';
    if (!showLimit) {
      w_e_max_length_info.textContent = '';
      if (editable) editable.style.paddingBottom = '0px';
      return;
    }

    if (editable) {
      editable.style.paddingBottom = '35px';
    }

    let label = '字符要求：';
    if (props.min && props.max) {
      label += `${props.min} ~ ${props.max}`;
    } else if (props.min) {
      label += `不少于 ${props.min}`;
    } else if (props.max) {
      label += `不多于 ${props.max}`;
    }
    w_e_max_length_info.textContent = `${label}，当前 ${count}`;

    let isError = false;
    if (props.min && props.min > 0 && count < props.min) {
      isError = true;
    }
    if (props.max && props.max > 0 && count > props.max) {
      isError = true;
    }
    w_e_max_length_info.style.color = isError
      ? 'var(--el-color-danger-light-3)'
      : 'var(--el-color-primary-light-3)';
  };

  watch(
    () => [props.min, props.max],
    () => {
      if (!props.readOnly && editorInstanceRef.value) {
        countValidCharsStrict(editorInstanceRef.value);
      }
    },
    { flush: 'post' }
  );

  watch(
    () => props.modelValue,
    (value: ValueType) => {
      if (value !== currContent && !isEqual(currContent, value)) {
        currContent = toRaw(value);
        render();
      }
    }
  );

  watch(
    () => props.readOnly,
    (_readOnly) => {
      if (!editorRef.value) return;
      render();
    }
  );

  onMounted(() => {
    render();
  });
  onBeforeUnmount(() => {
    destory();
  });
  onActivated(() => {
    render();
  });
  onDeactivated(() => {
    destory();
  });
</script>

<script lang="ts">
  export default {
    name: 'LzdEditor'
  };
</script>
